import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  UserCheck,
  RefreshCw,
  Headphones,
  Scale,
  Lock,
  Wallet,
  CheckCircle2,
} from "lucide-react";

interface ProtectionChecklistProps {
  type: "seller" | "buyer";
  onAnimationComplete: () => void;
  amount: number;
}

const ANIMATION_DELAY = 350; // ms between each item

const sellerProtectionItems = [
  { label: "Escrow protection activated" },
  { label: "Buyer identity verified" },
  { label: "Guaranteed payment on delivery" },
  { label: "Dispute resolution support" },
];

const buyerProtectionItems = [
  { label: "Seller KYC verified" },
  { label: "Funds secured in escrow" },
  { label: "Full refund eligibility" },
  { label: "24/7 dispute support" },
  { label: "Money-back guarantee" },
];

const getIconForIndex = (index: number, type: "seller" | "buyer") => {
  const iconClass = "w-4 h-4 text-green-500";

  if (type === "seller") {
    const icons = [
      <Shield className={iconClass} />,
      <UserCheck className={iconClass} />,
      <Wallet className={iconClass} />,
      <Scale className={iconClass} />,
    ];
    return icons[index] || <CheckCircle2 className={iconClass} />;
  } else {
    const icons = [
      <UserCheck className={iconClass} />,
      <Lock className={iconClass} />,
      <RefreshCw className={iconClass} />,
      <Headphones className={iconClass} />,
      <Shield className={iconClass} />,
    ];
    return icons[index] || <CheckCircle2 className={iconClass} />;
  }
};

interface AnimatedItemProps {
  icon: React.ReactNode;
  label: string;
  index: number;
}

function AnimatedItem({ icon, label, index }: AnimatedItemProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: 0.5 + index * (ANIMATION_DELAY / 1000),
      }}
      className="flex items-center bg-white py-3 px-3.5 rounded-lg border border-green-100"
    >
      <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3">
        {icon}
      </div>
      <span className="flex-1 text-sm text-green-800 font-medium">{label}</span>
      <CheckCircle2 className="w-4 h-4 text-green-500 ml-2" />
    </motion.div>
  );
}

export function ProtectionChecklist({
  type,
  onAnimationComplete,
  amount,
}: ProtectionChecklistProps) {
  const items = type === "seller" ? sellerProtectionItems : buyerProtectionItems;

  useEffect(() => {
    // Calculate total animation time and trigger completion
    const totalAnimationTime = 500 + items.length * ANIMATION_DELAY + 400;
    const timer = setTimeout(() => {
      onAnimationComplete();
    }, totalAnimationTime);

    return () => clearTimeout(timer);
  }, [items.length, onAnimationComplete]);

  return (
    <div className="bg-green-50 rounded-2xl p-5 mb-4 border border-green-200">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="text-center mb-4"
      >
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <Shield className="w-7 h-7 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-green-800">
          {type === "seller" ? "Deal Protection Enabled" : "Payment Protected"}
        </h3>
        <p className="text-sm text-green-700 mt-1">
          {type === "seller"
            ? `For deal amount of ₹${amount.toLocaleString("en-IN")}`
            : `Securing ₹${amount.toLocaleString("en-IN")} in escrow`}
        </p>
      </motion.div>

      {/* Divider */}
      <div className="h-px bg-green-200 my-3" />

      {/* Protection Items */}
      <div className="space-y-2.5">
        {items.map((item, index) => (
          <AnimatedItem
            key={index}
            icon={getIconForIndex(index, type)}
            label={item.label}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

export default ProtectionChecklist;
